import {
  metricKeys,
  type CaptureProfile,
  type MetricKey,
  type MetricRecognitionIntervalMs,
} from '@/domain/types'

export const MIN_CAPTURE_FPS = 1
export const DEFAULT_CAPTURE_FPS = 2
export const MAX_CAPTURE_FPS = 8
export const METRIC_CAPTURE_FPS_VERSION = 5
export const METRIC_RECOGNITION_INTERVAL_VERSION = 1

export const metricRecognitionIntervalOptions = [
  10_000,
  5_000,
  4_000,
  3_000,
  2_000,
  1_000,
  500,
  250,
] as const satisfies readonly Exclude<MetricRecognitionIntervalMs, 0>[]

const requiredRecognitionMetrics = new Set<MetricKey>(['level', 'exp'])
const DEFAULT_METRIC_RECOGNITION_INTERVALS_MS: Readonly<Record<MetricKey, MetricRecognitionIntervalMs>> = {
  level: 4_000,
  exp: 1_000,
  hp: 1_000,
  mp: 1_000,
  mesos: 1_000,
  mapName: 4_000,
}
const LEGACY_OCR_BASE_INTERVAL_MS: Readonly<Record<MetricKey, number>> = {
  level: 5_000,
  exp: 1_000,
  hp: 1_000,
  mp: 1_000,
  mesos: 1_000,
  mapName: 3_000,
}

const RECOGNITION_INTERVAL_REFERENCE_FPS = 4
const PREVIOUS_DEFAULT_CAPTURE_FPS = 4
const PREVIOUS_EXP_CAPTURE_FPS = 8
const PREVIOUS_MESOS_CAPTURE_FPS = 8

export type MetricRecognitionIntervalOverrides = Partial<Record<MetricKey, MetricRecognitionIntervalMs>>

export function normalizeCaptureFps(value: number | undefined): number {
  if (!Number.isFinite(value)) return DEFAULT_CAPTURE_FPS
  return Math.min(MAX_CAPTURE_FPS, Math.max(MIN_CAPTURE_FPS, Math.round(value!)))
}

export function recommendedMetricRecognitionIntervalsMs(): Record<MetricKey, MetricRecognitionIntervalMs> {
  return { ...DEFAULT_METRIC_RECOGNITION_INTERVALS_MS }
}

export function metricCanDisableRecognition(metric: MetricKey): boolean {
  return !requiredRecognitionMetrics.has(metric)
}

export function normalizeMetricRecognitionIntervalMs(
  metric: MetricKey,
  value: number | undefined,
): MetricRecognitionIntervalMs {
  if (value === 0) {
    return metricCanDisableRecognition(metric) ? 0 : DEFAULT_METRIC_RECOGNITION_INTERVALS_MS[metric]
  }
  return metricRecognitionIntervalOptions.includes(value as Exclude<MetricRecognitionIntervalMs, 0>)
    ? value as MetricRecognitionIntervalMs
    : DEFAULT_METRIC_RECOGNITION_INTERVALS_MS[metric]
}

export function normalizeMetricRecognitionIntervalsMs(
  values: Readonly<Partial<Record<MetricKey, number>>> | undefined,
): Record<MetricKey, MetricRecognitionIntervalMs> {
  return Object.fromEntries(metricKeys.map((metric) => [
    metric,
    normalizeMetricRecognitionIntervalMs(metric, values?.[metric]),
  ])) as Record<MetricKey, MetricRecognitionIntervalMs>
}

export function migrateRecognitionFrequencies(
  recognition: CaptureProfile['recognition'],
): CaptureProfile['recognition'] {
  let captureFps = normalizeCaptureFps(recognition.captureFps)
  const intervalVersion = recognition.metricRecognitionIntervalsVersion ?? 0
  const metricRecognitionIntervalsMs = intervalVersion >= METRIC_RECOGNITION_INTERVAL_VERSION
    ? normalizeMetricRecognitionIntervalsMs(recognition.metricRecognitionIntervalsMs)
    : migrateLegacyMetricIntervals(recognition)

  if (intervalVersion >= METRIC_RECOGNITION_INTERVAL_VERSION) {
    return {
      captureFps,
      metricRecognitionIntervalsMs,
      metricRecognitionIntervalsVersion: METRIC_RECOGNITION_INTERVAL_VERSION,
      minConfidence: recognition.minConfidence,
      ocrChangeThreshold: recognition.ocrChangeThreshold,
    }
  }

  if (captureFps === PREVIOUS_DEFAULT_CAPTURE_FPS) {
    captureFps = DEFAULT_CAPTURE_FPS
  }
  return {
    captureFps,
    metricRecognitionIntervalsMs,
    metricRecognitionIntervalsVersion: METRIC_RECOGNITION_INTERVAL_VERSION,
    minConfidence: recognition.minConfidence,
    ocrChangeThreshold: recognition.ocrChangeThreshold,
  }
}

function migrateLegacyMetricIntervals(
  recognition: CaptureProfile['recognition'],
): Record<MetricKey, MetricRecognitionIntervalMs> {
  const defaults = recommendedMetricRecognitionIntervalsMs()
  const version = recognition.metricCaptureFpsVersion ?? 0

  const migrated = { ...defaults }
  for (const metric of metricKeys) {
    const raw = recognition.metricCaptureFps?.[metric] ?? recognition.captureFps
    if (!Number.isFinite(raw)) continue
    const fps = normalizeCaptureFps(raw)
    if (fps === legacyMetricDefault(version, metric)) continue
    const effectiveInterval = Math.round(LEGACY_OCR_BASE_INTERVAL_MS[metric] * RECOGNITION_INTERVAL_REFERENCE_FPS / fps)
    migrated[metric] = closestRecognitionInterval(effectiveInterval)
  }
  return migrated
}

function legacyMetricDefault(version: number, metric: MetricKey): number {
  if (version >= METRIC_CAPTURE_FPS_VERSION) return DEFAULT_CAPTURE_FPS
  return previousMetricDefault(version, metric)
}

function closestRecognitionInterval(value: number): MetricRecognitionIntervalMs {
  return metricRecognitionIntervalOptions.reduce<Exclude<MetricRecognitionIntervalMs, 0>>((closest, candidate) => {
    const distance = Math.abs(candidate - value)
    const closestDistance = Math.abs(closest - value)
    return distance < closestDistance || (distance === closestDistance && candidate > closest)
      ? candidate
      : closest
  }, metricRecognitionIntervalOptions[0])
}

function previousMetricDefault(version: number, metric: MetricKey): number {
  if (metric === 'exp' && version >= 3) return PREVIOUS_EXP_CAPTURE_FPS
  if (metric === 'mesos' && version >= 4) return PREVIOUS_MESOS_CAPTURE_FPS
  return PREVIOUS_DEFAULT_CAPTURE_FPS
}

export function effectiveMetricRecognitionIntervalMs(
  recognition: CaptureProfile['recognition'],
  metric: MetricKey,
): MetricRecognitionIntervalMs {
  return normalizeMetricRecognitionIntervalMs(
    metric,
    recognition.metricRecognitionIntervalsMs?.[metric],
  )
}

export function metricRecognitionEnabled(
  recognition: CaptureProfile['recognition'],
  metric: MetricKey,
): boolean {
  return effectiveMetricRecognitionIntervalMs(recognition, metric) > 0
}

export function recognitionSchedulerIntervalMs(
  recognition: CaptureProfile['recognition'],
): number {
  const demandPerMs = metricKeys.reduce((total, metric) => {
    const interval = effectiveMetricRecognitionIntervalMs(recognition, metric)
    return interval > 0 ? total + 1 / interval : total
  }, 0)
  if (demandPerMs <= 0) return 5_000
  return Math.max(125, Math.min(5_000, Math.round(1 / demandPerMs)))
}

export function metricRecognitionRefreshIntervalMs(
  recognition: CaptureProfile['recognition'],
  metric: MetricKey,
  baseIntervalMs: number,
  baseMaximumIntervalMs: number,
): number {
  const interval = effectiveMetricRecognitionIntervalMs(recognition, metric)
  if (interval <= 0) return Number.POSITIVE_INFINITY
  const multiplier = Math.max(1, baseMaximumIntervalMs / Math.max(1, baseIntervalMs))
  return Math.max(interval, Math.round(interval * multiplier))
}
